import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPlus,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import QuestionEditor from "../QuestionEditor";
import { apiPost, apiPut, apiGet } from "../../../utils/apiAuth";
import config from "../../../config";
import tagsConfig from "../../../config/tags.json";
import { ContentTypeConstants } from "../../../constants/contentCreationConstants";

const ProjectSidebar = forwardRef(
  (
    {
      containerStatus = null,
      setContainerStatus = () => {},
      validationResults = null,
      a = null,
      pollingRef = null,
      onEditorReadyRef = null,
      isReadOnly = false,
    },
    ref
  ) => {
    //  --------------------------------------------------------------
    // Step 1: State Management
    // ---------------------------------------------------------------
    const [isFormCollapsed, setIsFormCollapsed] = useState(false);
    const [pageName, setPageName] = useState("");
    const [imageUrls, setImageUrls] = useState([]);

    // Gitea repo state
    const [giteaRepoUrl, setGiteaRepoUrl] = useState("");
    const [giteaTag, setGiteaTag] = useState("");
    const [isCreatingContainer, setIsCreatingContainer] = useState(false);
    const [containerError, setContainerError] = useState(null);
    const [containerName, setContainerName] = useState(null);

    // Validation errors
    const [validationErrors, setValidationErrors] = useState({});

    // Docker image - get from sessionStorage or use default
    const [selectedImage, setSelectedImage] = useState(() => {
      const stored = sessionStorage.getItem("projectCoding_dockerImage");
      return stored || "flask-project-mysql:v1";
    });

    // Tags state
    const [tags, setTags] = useState([]);

    // Duration state (in minutes only)
    const [durationMinutes, setDurationMinutes] = useState(0);
    
    // Template repo URL state
    const [templateRepoUrl, setTemplateRepoUrl] = useState("");

    // Docker image selection state
    const [showDockerImageDialog, setShowDockerImageDialog] = useState(false);
    const [dockerImages, setDockerImages] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedDockerImageId, setSelectedDockerImageId] = useState(null);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [projectImageUrlNull, setProjectImageUrlNull] = useState(false);

    // Build new image form state
    const [showBuildForm, setShowBuildForm] = useState(false);
    const [buildImageName, setBuildImageName] = useState("");
    const [buildImageTag, setBuildImageTag] = useState("latest");
    const [buildDescription, setBuildDescription] = useState("");
    const [buildFormTags, setBuildFormTags] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [selectedExtensions, setSelectedExtensions] = useState([]);
    const [isBuilding, setIsBuilding] = useState(false);
    const buildStatusIntervalRef = useRef(null);
    const [buildProgress, setBuildProgress] = useState({
      imageId: null,
      imageName: "",
      status: null,
      percentage: 0,
      message: "",
    });

    // Requirements editor: seed + resetKey so Jodit does not remount/reset on every keystroke
    const [requirementsSeed, setRequirementsSeed] = useState(
      () => sessionStorage.getItem("currentQuestionContent") || ""
    );
    const [requirementsResetKey, setRequirementsResetKey] = useState(() => {
      const qid =
        sessionStorage.getItem("currentQuestionId") ||
        sessionStorage.getItem("contentCreation_selectedQuestion") ||
        "new";
      try {
        return typeof qid === "string" && qid.startsWith('"')
          ? JSON.parse(qid)
          : qid;
      } catch {
        return qid || "new";
      }
    });
    const requirementsSeedQuestionRef = useRef(requirementsResetKey);
    const requirementsDirtyRef = useRef(false);
    const requirementsSeededHtmlRef = useRef(requirementsSeed);

    // Remount Jodit only when the question changes, or once on forced API prefill.
    // Never feed keystrokes back through `value` (that refreshes the editor).
    const syncRequirementsEditor = useCallback(
      (questionId, content, { force = false } = {}) => {
        const qid = questionId || "new";
        const html = content || "";
        const questionChanged = requirementsSeedQuestionRef.current !== qid;

        if (!questionChanged && !force) return;
        if (!questionChanged && force && requirementsDirtyRef.current) return;
        if (
          !questionChanged &&
          force &&
          requirementsSeededHtmlRef.current === html
        ) {
          return;
        }

        requirementsSeedQuestionRef.current = qid;
        requirementsDirtyRef.current = false;
        requirementsSeededHtmlRef.current = html;
        setRequirementsSeed(html);
        setRequirementsResetKey(questionChanged ? qid : `${qid}-prefill`);
      },
      []
    );

    // Prefill form from API when question_id exists (user revisits saved record)
    const prefilledQuestionIdRef = useRef(null);
    useEffect(() => {
      const fetchAndPrefillQuestion = async () => {
        let currentQuestionId = sessionStorage.getItem("currentQuestionId");
        if (!currentQuestionId || currentQuestionId.trim() === "") {
          const selectedQuestion = sessionStorage.getItem(
            "contentCreation_selectedQuestion"
          );
          if (selectedQuestion) {
            try {
              const parsed = JSON.parse(selectedQuestion);
              currentQuestionId = parsed && typeof parsed === "string" ? parsed : selectedQuestion;
            } catch {
              currentQuestionId = selectedQuestion;
            }
            if (currentQuestionId) sessionStorage.setItem("currentQuestionId", currentQuestionId);
          }
        }
        if (!currentQuestionId || currentQuestionId.trim() === "" || prefilledQuestionIdRef.current === currentQuestionId) {
          return;
        }
        try {
          const response = await apiGet(
            `${config.API_BASE_URL}/api/content_creation/course-plan/${encodeURIComponent(currentQuestionId.trim())}/`
          );
          const jsonData = response.data?.question_data;
          if (!jsonData || typeof jsonData !== "object") return;
          let latestQuestionId = sessionStorage.getItem("currentQuestionId") || sessionStorage.getItem("contentCreation_selectedQuestion");
          try {
            if (latestQuestionId) latestQuestionId = typeof latestQuestionId === "string" && latestQuestionId.startsWith("\"") ? JSON.parse(latestQuestionId) : latestQuestionId;
          } catch (_) {}
          if (latestQuestionId && String(latestQuestionId).trim() !== currentQuestionId.trim()) return;
          prefilledQuestionIdRef.current = currentQuestionId;

          if (jsonData.Qn) {
            sessionStorage.setItem("projectCoding_pageName", jsonData.Qn);
            setPageName(jsonData.Qn);
          }
          if (jsonData.Tags && Array.isArray(jsonData.Tags)) {
            sessionStorage.setItem("projectCoding_tags", JSON.stringify(jsonData.Tags));
            setTags(jsonData.Tags);
          }
          if (jsonData.image_urls && Array.isArray(jsonData.image_urls)) {
            sessionStorage.setItem("projectCoding_imageUrls", JSON.stringify(jsonData.image_urls));
            setImageUrls(jsonData.image_urls);
          }
          if (jsonData.master_repo_url) {
            sessionStorage.setItem("projectCoding_giteaRepoUrl", jsonData.master_repo_url);
            sessionStorage.setItem("projectCoding_githubRepoUrl", jsonData.master_repo_url);
            setGiteaRepoUrl(jsonData.master_repo_url);
          }
          if (jsonData.master_repo_tag) {
            sessionStorage.setItem("projectCoding_giteaTag", jsonData.master_repo_tag);
            sessionStorage.setItem("projectCoding_githubTag", jsonData.master_repo_tag);
            setGiteaTag(jsonData.master_repo_tag);
          }
          if (jsonData.master_commit_sha) {
            sessionStorage.setItem(
              "projectCoding_masterCommitSha",
              jsonData.master_commit_sha
            );
          }
          if (jsonData.template_repo_url) {
            sessionStorage.setItem("projectCoding_templateRepoUrl", jsonData.template_repo_url);
            setTemplateRepoUrl(jsonData.template_repo_url);
          }
          if (jsonData.docker_image) {
            sessionStorage.setItem("projectCoding_dockerImage", jsonData.docker_image);
            setSelectedImage(jsonData.docker_image);
          }
          if (jsonData.duration_minutes !== undefined && jsonData.duration_minutes !== null) {
            const dur = parseInt(jsonData.duration_minutes, 10);
            if (!isNaN(dur)) {
              sessionStorage.setItem("projectCoding_durationMinutes", String(dur));
              setDurationMinutes(dur);
            }
          }
          if (jsonData.image_id !== undefined && jsonData.image_id !== null) {
            sessionStorage.setItem("projectCoding_imageId", String(jsonData.image_id));
            setProjectImageUrlNull(false);
          }
          const questionContent = jsonData.Name || jsonData.requirements || "";
          if (questionContent) {
            sessionStorage.setItem("currentQuestionContent", questionContent);
          }
          syncRequirementsEditor(currentQuestionId, questionContent || "", {
            force: true,
          });
        } catch (err) {
          console.error("Error prefilling project question:", err);
        }
      };
      fetchAndPrefillQuestion();
    }, [syncRequirementsEditor]);

    // Load question data from sessionStorage when component mounts or when question changes
    useEffect(() => {
      const loadQuestionData = () => {
        // Check for contentCreation_selectedQuestion and set currentQuestionId if not already set
        // This handles the case when navigating from ProjectContentBank
        const currentQuestionId = sessionStorage.getItem("currentQuestionId");
        if (!currentQuestionId || currentQuestionId.trim() === "") {
          const selectedQuestion = sessionStorage.getItem(
            "contentCreation_selectedQuestion"
          );
          if (selectedQuestion) {
            try {
              const questionId = JSON.parse(selectedQuestion);
              if (questionId && questionId.trim() !== "") {
                sessionStorage.setItem("currentQuestionId", questionId);
              }
            } catch (e) {
              // If parsing fails, try using it directly as string
              if (selectedQuestion && selectedQuestion.trim() !== "") {
                sessionStorage.setItem("currentQuestionId", selectedQuestion);
              }
            }
          }
        }

        // Load page name (only update if changed to avoid re-renders that trigger Jodit blob URLs)
        const storedPageName = sessionStorage.getItem("projectCoding_pageName");
        if (storedPageName !== undefined && storedPageName !== null) {
          setPageName((prev) => (prev !== storedPageName ? storedPageName : prev));
        }

        // Load tags
        const storedTags = sessionStorage.getItem("projectCoding_tags");
        if (storedTags) {
          try {
            const parsedTags = JSON.parse(storedTags);
            if (Array.isArray(parsedTags)) {
              setTags((prev) =>
                JSON.stringify(prev) !== storedTags ? parsedTags : prev
              );
            }
          } catch (_) {}
        }

        // Load image URLs
        const storedImageUrls = sessionStorage.getItem(
          "projectCoding_imageUrls"
        );
        if (storedImageUrls) {
          try {
            const parsedImageUrls = JSON.parse(storedImageUrls);
            if (Array.isArray(parsedImageUrls)) {
              setImageUrls((prev) =>
                JSON.stringify(prev) !== storedImageUrls ? parsedImageUrls : prev
              );
            }
          } catch (_) {}
        }

        // Load Gitea repo URL (giteaRepoUrl or githubRepoUrl from API)
        const storedGiteaRepoUrl =
          sessionStorage.getItem("projectCoding_giteaRepoUrl") ||
          sessionStorage.getItem("projectCoding_githubRepoUrl");
        if (storedGiteaRepoUrl !== undefined && storedGiteaRepoUrl !== null) {
          setGiteaRepoUrl((prev) =>
            prev !== storedGiteaRepoUrl ? storedGiteaRepoUrl : prev
          );
        }

        // Load Gitea tag (giteaTag or githubTag from API)
        const storedGiteaTag =
          sessionStorage.getItem("projectCoding_giteaTag") ||
          sessionStorage.getItem("projectCoding_githubTag");
        if (storedGiteaTag !== undefined && storedGiteaTag !== null) {
          setGiteaTag((prev) =>
            prev !== storedGiteaTag ? storedGiteaTag : prev
          );
        }

        // Load Docker image
        const storedDockerImage = sessionStorage.getItem(
          "projectCoding_dockerImage"
        );
        if (storedDockerImage) {
          setSelectedImage((prev) =>
            prev !== storedDockerImage ? storedDockerImage : prev
          );
        }

        // Check if project_image_url is null (stored in sessionStorage from get-question-heirarchial)
        const storedImageId = sessionStorage.getItem("projectCoding_imageId");
        const imageUrlNull = !storedImageId || storedImageId === "null";
        setProjectImageUrlNull((prev) => (prev !== imageUrlNull ? imageUrlNull : prev));

        // Load duration
        const storedDuration = sessionStorage.getItem(
          "projectCoding_durationMinutes"
        );
        if (storedDuration) {
          const duration = parseInt(storedDuration);
          if (!isNaN(duration)) {
            setDurationMinutes((prev) => (prev !== duration ? duration : prev));
          }
        }

        // Load template repo URL
        const storedTemplateRepoUrl = sessionStorage.getItem(
          "projectCoding_templateRepoUrl"
        );
        if (storedTemplateRepoUrl !== undefined && storedTemplateRepoUrl !== null) {
          setTemplateRepoUrl((prev) =>
            prev !== storedTemplateRepoUrl ? storedTemplateRepoUrl : prev
          );
        }

        // Sync requirements editor only when the question id changes (not on typing / poll ticks)
        let qid =
          sessionStorage.getItem("currentQuestionId") ||
          sessionStorage.getItem("contentCreation_selectedQuestion") ||
          "new";
        try {
          if (typeof qid === "string" && qid.startsWith('"')) qid = JSON.parse(qid);
        } catch (_) {}
        const storedRequirements =
          sessionStorage.getItem("currentQuestionContent") || "";
        syncRequirementsEditor(qid, storedRequirements);
      };

      loadQuestionData();

      // Also listen for storage events to update when question changes
      const handleStorageChange = (e) => {
        // Listen for projectCoding_ keys, currentQuestionId, and contentCreation_selectedQuestion changes
        if (
          e.key &&
          (e.key.startsWith("projectCoding_") ||
            e.key === "currentQuestionId" ||
            e.key === "contentCreation_selectedQuestion" ||
            e.key === "contentCreation_projectId")
        ) {
          loadQuestionData(); // This will check project_image_url status
        }
      };

      window.addEventListener("storage", handleStorageChange);

      // Poll sessionStorage periodically to catch changes from same window
      // Note: storage event only fires for OTHER windows/tabs; use longer interval to avoid
      // frequent re-renders that cause Jodit (requirements editor) to recreate blob URLs
      const interval = setInterval(() => {
        loadQuestionData();
      }, 3000);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(interval);
      };
    }, [syncRequirementsEditor]);

    // Persist sidebar form data to sessionStorage on change (survives reload until session clears)
    useEffect(() => {
      try {
        sessionStorage.setItem("projectCoding_pageName", pageName || "");
        sessionStorage.setItem("projectCoding_giteaRepoUrl", giteaRepoUrl || "");
        sessionStorage.setItem("projectCoding_giteaTag", giteaTag || "");
        sessionStorage.setItem("projectCoding_tags", JSON.stringify(tags || []));
        sessionStorage.setItem("projectCoding_imageUrls", JSON.stringify(imageUrls || []));
        sessionStorage.setItem("projectCoding_durationMinutes", String(durationMinutes ?? 0));
        sessionStorage.setItem("projectCoding_templateRepoUrl", templateRepoUrl || "");
      } catch (e) {
        // Ignore quota/storage errors
      }
    }, [pageName, giteaRepoUrl, giteaTag, tags, imageUrls, durationMinutes, templateRepoUrl]);

    useEffect(() => {
      if (!containerStatus?.success) {
        setContainerName(null);
        setIsCreatingContainer(false);
        return;
      }
      const id =
        containerStatus.containerName ||
        containerStatus.containerId ||
        null;
      if (id) {
        setContainerName(id);
      }
    }, [containerStatus]);

    // Editor readiness polling (in ProjectVscEditor) signals completion here so the
    // "Creating..." button state persists until the pod is ready / editor loaded.
    useEffect(() => {
      if (!onEditorReadyRef) return;
      onEditorReadyRef.current = () => {
        setIsCreatingContainer(false);
      };
      return () => {
        if (onEditorReadyRef) onEditorReadyRef.current = null;
      };
    }, [onEditorReadyRef]);

    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveFieldHighlight, setSaveFieldHighlight] = useState({
      giteaRepo: false,
      giteaTag: false,
      taskName: false,
      requirements: false,
      tags: false,
      templateRepo: false,
      duration: false,
    });
    const giteaSectionRef = useRef(null);
    const titleInputRef = useRef(null);
    const requirementsWrapRef = useRef(null);
    const tagsWrapRef = useRef(null);
    const templateRepoWrapRef = useRef(null);
    const durationInputRef = useRef(null);

    const handleRequirementsChange = useCallback((html) => {
      requirementsDirtyRef.current = true;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html || "";
      const textContent = tempDiv.textContent || tempDiv.innerText || "";
      if (textContent.trim()) {
        setSaveFieldHighlight((s) =>
          s.requirements ? { ...s, requirements: false } : s
        );
      }
    }, []);

    //  --------------------------------------------------------------
    // Step 2: Utility Functions
    // ---------------------------------------------------------------

    // Handle Gitea URL validation
    const validateGiteaUrl = (url) => {
      if (!url) return false;
      // Gitea URLs can be on any domain: https://gitea.example.com/owner/repo
      const giteaPattern =
        /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-\.]+)*\/[\w\-\.]+\/[\w\-\.]+(\/)?$/;
      return giteaPattern.test(url);
    };

    const normalizeRepoUrl = (url) =>
      (url || "").trim().replace(/\/+$/, "").toLowerCase();

    const isSameRepoUrl = (masterUrl, templateUrl) => {
      const master = normalizeRepoUrl(masterUrl);
      const template = normalizeRepoUrl(templateUrl);
      return Boolean(master && template && master === template);
    };

    const resolveMasterRepoCommitSha = async (repoUrl, repoTag) => {
      const response = await apiPost(
        `${config.API_BASE_URL}/api/content_creation/gitea/resolve-commit-sha/`,
        {
          master_repo_url: repoUrl.trim(),
          master_repo_tag: repoTag.trim(),
        }
      );
      return (
        response.data?.commit_sha ||
        response.data?.data?.commit_sha ||
        null
      );
    };

    // Fetch docker images with optional tag filters
    const fetchDockerImages = async (tagFilters = []) => {
      setIsLoadingImages(true);
      try {
        const filtersParam = tagFilters.length > 0 ? tagFilters.join(",") : null;
        const url = filtersParam
          ? `${config.API_BASE_URL}/api/docker/images?filters=${encodeURIComponent(filtersParam)}`
          : `${config.API_BASE_URL}/api/docker/images`;
        
        const response = await apiGet(url);
        if (response.data && response.data.success) {
          setDockerImages(response.data.images || []);
          setAvailableTags(response.data.availableFilters || []);
        }
      } catch (error) {
        console.error("Error fetching docker images:", error);
        setDockerImages([]);
        setAvailableTags([]);
      } finally {
        setIsLoadingImages(false);
      }
    };

    // Handle tag selection toggle
    const handleTagToggle = (tag) => {
      setSelectedTags((prev) => {
        const newTags = prev.includes(tag)
          ? prev.filter((t) => t !== tag)
          : [...prev, tag];
        // Refetch images with new filters
        fetchDockerImages(newTags);
        return newTags;
      });
    };

    // Handle docker image selection
    const handleDockerImageSelect = async () => {
      if (!selectedDockerImageId) {
        setContainerError("Please select a docker image");
        return;
      }

      try {
        const projectId = sessionStorage.getItem("contentCreation_projectId");
        const projectIdInt = projectId ? parseInt(JSON.parse(projectId)) : null;

        if (!projectIdInt) {
          setContainerError("Project ID not found");
          return;
        }

        const response = await apiPut(
          `${config.API_BASE_URL}/api/admin/project_image_url/update/`,
          {
            project_id: projectIdInt,
            image_url: selectedDockerImageId,
          }
        );

        if (response.data) {
          // Update sessionStorage
          const selectedImage = dockerImages.find(
            (img) => img.id === selectedDockerImageId
          );
          if (selectedImage) {
            sessionStorage.setItem(
              "projectCoding_dockerImage",
              `${selectedImage.image_name}:${selectedImage.image_tag}`
            );
            sessionStorage.setItem(
              "projectCoding_imageId",
              String(selectedDockerImageId)
            );
            setSelectedImage(
              `${selectedImage.image_name}:${selectedImage.image_tag}`
            );
          }
          setProjectImageUrlNull(false);
          setShowDockerImageDialog(false);
          setContainerError(null);
        }
      } catch (error) {
        console.error("Error updating project image URL:", error);
        setContainerError(
          error.response?.data?.detail || "Failed to update docker image"
        );
      }
    };

    // Open docker image dialog
    const handleOpenDockerImageDialog = () => {
      setShowDockerImageDialog(true);
      setSelectedTags([]);
      setSelectedDockerImageId(null);
      setShowBuildForm(false);
      fetchDockerImages();
    };

    // Get packages and extensions based on selected tags
    const getPackagesAndExtensionsByTags = (tags) => {
      const allPackages = { pip: [], npm: [], apt: [] };
      const allExtensions = [];

      tags.forEach((tag) => {
        const tagLower = tag.toLowerCase();
        if (tagsConfig.tags[tagLower]) {
          const tagData = tagsConfig.tags[tagLower];
          // Merge packages
          if (tagData.packages) {
            Object.keys(tagData.packages).forEach((pkgType) => {
              if (tagData.packages[pkgType]) {
                allPackages[pkgType] = [
                  ...allPackages[pkgType],
                  ...tagData.packages[pkgType],
                ];
              }
            });
          }
          // Merge extensions
          if (tagData.extensions) {
            allExtensions.push(...tagData.extensions);
          }
        }
      });

      // Remove duplicates
      Object.keys(allPackages).forEach((pkgType) => {
        allPackages[pkgType] = [...new Set(allPackages[pkgType])].sort();
      });
      const uniqueExtensions = [...new Set(allExtensions)].sort();

      return { packages: allPackages, extensions: uniqueExtensions };
    };

    // Handle build form tag toggle
    const handleBuildFormTagToggle = (tag) => {
      setBuildFormTags((prev) => {
        const newTags = prev.includes(tag)
          ? prev.filter((t) => t !== tag)
          : [...prev, tag];
        // Update packages and extensions based on tags
        const { packages, extensions } = getPackagesAndExtensionsByTags(newTags);
        // Reset selections when tags change
        setSelectedPackages([]);
        setSelectedExtensions([]);
        return newTags;
      });
    };

    // Handle package toggle
    const handlePackageToggle = (pkgType, pkgName) => {
      const pkgKey = `${pkgType}:${pkgName}`;
      setSelectedPackages((prev) =>
        prev.includes(pkgKey)
          ? prev.filter((p) => p !== pkgKey)
          : [...prev, pkgKey]
      );
    };

    // Handle extension toggle
    const handleExtensionToggle = (extId) => {
      setSelectedExtensions((prev) =>
        prev.includes(extId)
          ? prev.filter((e) => e !== extId)
          : [...prev, extId]
      );
    };

    // Handle build image submission
    const handleBuildImage = async () => {
      if (!buildImageName || !buildImageName.trim()) {
        setContainerError("Please enter an image name");
        return;
      }

      if (buildFormTags.length === 0) {
        setContainerError("Please select at least one tag");
        return;
      }

      setIsBuilding(true);
      setContainerError(null);

      try {
        // Get current user email from session or token
        const userEmail =
          sessionStorage.getItem("userEmail") ||
          sessionStorage.getItem("email") ||
          "system";

        // Prepare packages array
        const packagesArray = selectedPackages.map((pkgKey) => {
          const [type, name] = pkgKey.split(":");
          return { name, type, version: null };
        });

        // Prepare extensions array
        const extensionsArray = selectedExtensions.map((extId) => ({
          id: extId,
          version: null,
        }));

        // Step 1: Create docker image configuration
        const createResponse = await apiPost(
          `${config.API_BASE_URL}/api/docker/images`,
          {
            image_name: buildImageName.trim(),
            image_tag: buildImageTag.trim() || "latest",
            description: buildDescription.trim() || null,
            tags: buildFormTags,
            packages: packagesArray,
            extensions: extensionsArray,
            created_by: userEmail,
          }
        );

        if (!createResponse.data || !createResponse.data.success) {
          throw new Error(
            createResponse.data?.error || "Failed to create image configuration"
          );
        }

        const imageId = createResponse.data.image_id;

        // Step 2: Trigger build (this will also push to registry if configured)
        const buildResponse = await apiPost(
          `${config.API_BASE_URL}/api/docker/builds/trigger/${imageId}?no_cache=false&push_to_registry=true`
        );

        if (!buildResponse.data || !buildResponse.data.success) {
          throw new Error(
            buildResponse.data?.error || "Failed to trigger build"
          );
        }

        // Start tracking build progress
        setBuildProgress({
          imageId: imageId,
          imageName: buildImageName.trim(),
          status: "pending",
          percentage: 10,
          message: "Build queued...",
        });

        // Start polling build status
        startBuildStatusPolling(imageId);

        // Don't close form yet - show progress
        // setShowBuildForm(false);
        // Reset form inputs but keep dialog open to show progress
        setBuildImageName("");
        setBuildImageTag("latest");
        setBuildDescription("");
        setBuildFormTags([]);
        setSelectedPackages([]);
        setSelectedExtensions([]);
      } catch (error) {
        console.error("Error creating and building image:", error);
        setContainerError(
          error.response?.data?.detail ||
            error.message ||
            "Failed to create and build docker image"
        );
      } finally {
        setIsBuilding(false);
      }
    };

    // Poll build status
    const startBuildStatusPolling = (imageId) => {
      // Clear any existing interval
      if (buildStatusIntervalRef.current) {
        clearInterval(buildStatusIntervalRef.current);
      }

      const pollInterval = setInterval(async () => {
        try {
          const response = await apiGet(
            `${config.API_BASE_URL}/api/docker/builds/status/${imageId}`
          );

          if (response.data && response.data.success) {
            const status = response.data.status;
            let percentage = 0;
            let message = "";

            // Calculate percentage based on status
            switch (status) {
              case "pending":
                percentage = 10;
                message = "Build queued...";
                break;
              case "building":
                percentage = 50;
                message = "Building image...";
                break;
              case "ready":
                percentage = 100;
                message = "Build completed!";
                break;
              case "failed":
                percentage = 0;
                message = "Build failed";
                break;
              default:
                percentage = 0;
                message = status;
            }

            setBuildProgress((prev) => ({
              ...prev,
              status: status,
              percentage: percentage,
              message: message,
            }));

            // Stop polling if build is complete (ready or failed)
            if (status === "ready" || status === "failed") {
              clearInterval(buildStatusIntervalRef.current);
              buildStatusIntervalRef.current = null;

              if (status === "ready") {
                // Refresh images list
                fetchDockerImages();
                // Close form after a delay
                setTimeout(() => {
                  setShowBuildForm(false);
                  setBuildProgress({
                    imageId: null,
                    imageName: "",
                    status: null,
                    percentage: 0,
                    message: "",
                  });
                }, 2000);
              } else {
                setContainerError("Build failed. Please check the logs.");
              }
            }
          }
        } catch (error) {
          console.error("Error polling build status:", error);
          // Continue polling on error
        }
      }, 3000); // Poll every 3 seconds

      buildStatusIntervalRef.current = pollInterval;
    };

    // Cleanup polling on unmount or dialog close
    useEffect(() => {
      return () => {
        if (buildStatusIntervalRef.current) {
          clearInterval(buildStatusIntervalRef.current);
          buildStatusIntervalRef.current = null;
        }
      };
    }, []);

    // Cleanup polling when dialog closes
    const handleCloseDialog = () => {
      if (buildStatusIntervalRef.current) {
        clearInterval(buildStatusIntervalRef.current);
        buildStatusIntervalRef.current = null;
      }
      setShowDockerImageDialog(false);
      setBuildProgress({
        imageId: null,
        imageName: "",
        status: null,
        percentage: 0,
        message: "",
      });
    };

    //  --------------------------------------------------------------
    // Step 3: Container Creation Handler
    // ---------------------------------------------------------------

    // Handle Docker container creation
    const handleCreateContainer = async () => {
      if (!validateGiteaUrl(giteaRepoUrl)) {
        setContainerError(
          "Please enter a valid Gitea repository URL (e.g., https://gitea.example.com/owner/repo)"
        );
        return;
      }

      if (!giteaTag || !giteaTag.trim()) {
        setContainerError(
          "Please enter a Gitea Version/Release (required for validation)"
        );
        return;
      }

      setIsCreatingContainer(true);
      setContainerError(null);
      setContainerStatus(null);
      // Clear container status from sessionStorage when creating a new container
      try {
        sessionStorage.removeItem("projectCoding_containerStatus");
      } catch (error) {}

      try {
        // Get image_id from sessionStorage (from project_image_url.id)
        const imageId = sessionStorage.getItem("projectCoding_imageId");

        // Prepare request payload
        const requestPayload = {
          repoUrl: giteaRepoUrl,
          gitTag: giteaTag.trim() || null,
        };

        // Use image_id if available, otherwise fall back to image name
        if (imageId) {
          requestPayload.image_id = parseInt(imageId);
        } else {
          requestPayload.image = selectedImage;
        }

        // Add batch_project_id if available from sessionStorage
        const batchProjectIdToSend = sessionStorage.getItem("batch_project_id");
        if (batchProjectIdToSend) {
          const batchId = parseInt(batchProjectIdToSend);
          if (!isNaN(batchId)) {
            requestPayload.batch_project_id = batchId;
          }
        }

        const response = await apiPost(
          "/api/docker/create-container",
          requestPayload,
          { timeout: 120000 }
        );

        if (response.data && response.data.success) {
          const containerId =
            response.data.containerId || response.data.containerName;
          setContainerName(containerId);
          const newContainerStatus = {
            success: true,
            containerId: containerId,
            containerName: containerId,
            containerUrl: response.data.containerUrl,
            student_id: response.data.student_id || response.data.studentId,
            message:
              response.data.message || "Container created successfully!",
            branch_switch_failed: Boolean(response.data.branch_switch_failed),
          };
          setContainerStatus(newContainerStatus);

          // Persist container status to sessionStorage
          try {
            sessionStorage.setItem(
              "projectCoding_containerStatus",
              JSON.stringify(newContainerStatus)
            );
          } catch (error) {}

          // Polling is started by ProjectVscEditor's useEffect when containerStatus updates
          if (newContainerStatus.branch_switch_failed) {
            setContainerError(newContainerStatus.message);
            setIsCreatingContainer(false);
          } else {
            setContainerError(null);
          }
        } else {
          setContainerError(
            response.data?.message ||
              response.data?.detail?.error ||
              response.data?.error ||
              "Failed to create container"
          );
          setIsCreatingContainer(false);
        }
      } catch (error) {
        const detail = error.response?.data?.detail;
        let detailMessage =
          typeof detail === "object" && detail !== null
            ? detail.error || detail.message
            : detail;
        if (!detailMessage && error.code === "ECONNABORTED") {
          detailMessage =
            "Container creation timed out. The cluster may be busy or ingress is unavailable - try again in a minute.";
        }
        if (
          typeof detailMessage === "string" &&
          /ingress|webhook|admission/i.test(detailMessage)
        ) {
          detailMessage =
            detailMessage +
            " This is a cluster networking issue (not browser/Mac). Ask ops to check ingress-nginx.";
        }
        setContainerError(
          detailMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Error creating container"
        );
        setIsCreatingContainer(false);
      }
    };

    //  --------------------------------------------------------------
    // Step 4: Image URL Handlers
    // ---------------------------------------------------------------

    // Image URL handlers
    const addImageUrl = () => {
      setImageUrls([...imageUrls, { actualUrl: "", expectedUrl: "" }]);
    };

    const removeImageUrl = (index) => {
      setImageUrls(imageUrls.filter((_, i) => i !== index));
    };

    const updateImageUrl = (index, field, value) => {
      const updated = [...imageUrls];
      updated[index][field] = value;
      setImageUrls(updated);
    };

    //  --------------------------------------------------------------

    // Tags handlers - matching CodingForm/MCQForm pattern
    const handleTagChange = (index, value) => {
      const updatedTags = [...tags];
      updatedTags[index] = value;
      setTags(updatedTags);
      if (updatedTags.some((t) => t && String(t).trim())) {
        setSaveFieldHighlight((s) => (s.tags ? { ...s, tags: false } : s));
      }
    };

    const addTag = () => {
      setTags([...tags, ""]);
    };

    const removeTag = (index) => {
      setTags(tags.filter((_, i) => i !== index));
    };

    const validateProjectCodingRequiredFields = useCallback(() => {
      const cleared = {
        giteaRepo: false,
        giteaTag: false,
        taskName: false,
        requirements: false,
        tags: false,
        templateRepo: false,
        duration: false,
      };
      const next = { ...cleared };
      const missingLabels = [];

      const repoTrim = (giteaRepoUrl || "").trim();
      if (!repoTrim) {
        next.giteaRepo = true;
        missingLabels.push("Gitea repository URL");
      } else if (!validateGiteaUrl(repoTrim)) {
        next.giteaRepo = true;
        missingLabels.push("a valid Gitea repository URL");
      }

      if (!giteaTag || !String(giteaTag).trim()) {
        next.giteaTag = true;
        missingLabels.push("Gitea version/release");
      }

      if (!pageName || !pageName.trim()) {
        next.taskName = true;
        missingLabels.push("task name");
      }

      const questionContent =
        sessionStorage.getItem("currentQuestionContent") || "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = questionContent;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";
      if (!questionContent || !textContent.trim()) {
        next.requirements = true;
        missingLabels.push("requirements");
      }

      const validTags = tags.filter((tag) => tag && tag.trim());
      if (validTags.length === 0) {
        next.tags = true;
        missingLabels.push("at least one tag");
      }

      const durationValue = Number(durationMinutes);
      if (!Number.isFinite(durationValue) || durationValue <= 0) {
        next.duration = true;
        missingLabels.push("duration (minutes)");
      }

      if (!templateRepoUrl || !String(templateRepoUrl).trim()) {
        next.templateRepo = true;
        missingLabels.push("template repo URL");
      } else if (isSameRepoUrl(giteaRepoUrl, templateRepoUrl)) {
        next.templateRepo = true;
        missingLabels.push(
          "a template repo URL different from the Gitea repository URL"
        );
      }

      const ok = missingLabels.length === 0;
      setSaveFieldHighlight(ok ? cleared : next);
      if (!ok) {
        setSaveError(
          `Please complete: ${missingLabels.join(", ")}`
        );
        setIsFormCollapsed(false);
      } else {
        setSaveError(null);
      }
      return ok;
    }, [pageName, tags, giteaRepoUrl, giteaTag, templateRepoUrl, durationMinutes]);

    useEffect(() => {
      const {
        giteaRepo,
        giteaTag,
        taskName,
        requirements,
        tags,
        templateRepo,
        duration,
      } = saveFieldHighlight;
      if (
        !giteaRepo &&
        !giteaTag &&
        !taskName &&
        !requirements &&
        !tags &&
        !templateRepo &&
        !duration
      ) {
        return;
      }
      const el =
        giteaRepo || giteaTag
          ? giteaSectionRef.current
          : taskName
            ? titleInputRef.current
            : requirements
              ? requirementsWrapRef.current
              : tags
                ? tagsWrapRef.current
                : duration
                  ? durationInputRef.current
                  : templateRepo
                    ? templateRepoWrapRef.current
                    : null;
      if (el && typeof el.scrollIntoView === "function") {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    }, [saveFieldHighlight]);

    //  --------------------------------------------------------------
    // Step 5: Save Handler
    // ---------------------------------------------------------------

    const handleSave = async () => {
      if (isReadOnly) {
        return null;
      }
      if (!validateProjectCodingRequiredFields()) {
        return;
      }

      // Get subtopic_id from sessionStorage
      const storedSubTopic =
        sessionStorage.getItem("contentCreation_selectedSubTopic") ||
        sessionStorage.getItem("selectedSubTopic");
      let subtopicId = storedSubTopic
        ? JSON.parse(storedSubTopic) || storedSubTopic
        : null;
      subtopicId = subtopicId ? String(subtopicId).trim() : null;

      if (!subtopicId) {
        setSaveError("Subtopic ID not found");
        return;
      }

      setIsSaving(true);
      setSaveError(null);

      try {
        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate()}-${
          currentDate.getMonth() + 1
        }-${currentDate.getFullYear()} ${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
        const createdBy = sessionStorage.getItem("Email") || "";

        // Get question content from sessionStorage
        const questionContent =
          sessionStorage.getItem("currentQuestionContent") || "";

        // Get level from sessionStorage
        const storedLevel = sessionStorage.getItem(
          "contentCreation_selectedDifficulty"
        );
        const level = storedLevel ? JSON.parse(storedLevel) : null;
        if (!level || !["level1", "level2", "level3"].includes(level)) {
          setSaveError("Invalid level");
          setIsSaving(false);
          return;
        }

        // Get contentType from sessionStorage
        const storedContentType = sessionStorage.getItem(
          "contentCreation_contentType"
        );
        const parsedContentType = storedContentType
          ? JSON.parse(storedContentType)
          : null;
        const questionType =
          parsedContentType === "Project Coding" ||
          parsedContentType === "project_coding"
            ? "project_coding"
            : "project_coding";

        // Build payload matching course-plan structure
        // Get currentQuestionId - this determines if we're updating or creating
        const currentQuestionId = sessionStorage.getItem("currentQuestionId");

        let masterCommitSha = null;
        try {
          masterCommitSha = await resolveMasterRepoCommitSha(
            giteaRepoUrl,
            giteaTag
          );
        } catch (shaError) {
          const detail = shaError.response?.data?.detail;
          const detailMessage =
            typeof detail === "object" && detail?.message
              ? detail.message
              : typeof detail === "string"
                ? detail
                : shaError.response?.data?.message;
          setSaveError(
            detailMessage ||
              "Could not resolve Gitea tag to commit SHA. Check repository URL and tag."
          );
          setIsSaving(false);
          return;
        }
        if (!masterCommitSha) {
          setSaveError(
            "Could not resolve Gitea tag to commit SHA. Check repository URL and tag."
          );
          setIsSaving(false);
          return;
        }

        const jsonObject = {
          subtopic_id: subtopicId,
          level: level,
          type: questionType,
          LastUpdated: "",
          CreatedON: formattedDate,
          CreatedBy: createdBy,
          Tags: tags.filter((tag) => tag && tag.trim()),
          Qn: pageName,
          Name: questionContent,
          question_type: questionType,
          question_types_enum: ContentTypeConstants.PROJECT_CODING.key,
          master_repo_url: giteaRepoUrl,
          master_repo_tag: giteaTag,
          master_commit_sha: masterCommitSha,
          template_repo_url: templateRepoUrl,
          docker_image: selectedImage,
          duration_minutes: durationMinutes,
          image_id: parseInt(sessionStorage.getItem("projectCoding_imageId")),
          image_urls: imageUrls,
          enum: questionType,
        };

        // Only include currentFile if currentQuestionId exists and is not empty (for updates)
        // This matches the pattern used in saveHTML for HTML/CSS content
        if (currentQuestionId && currentQuestionId.trim() !== "") {
          jsonObject.currentFile = currentQuestionId.trim();
        }

        // Backend handles both create and update via POST, using currentFile to determine operation
        const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/course-plan/`,
          jsonObject
        );

        if (response.status === 200) {
          setSaveError(null);

          // Get question_id from response (try common response fields)
          const questionIdFromResponse =
            response.data?.question_id ||
            response.data?.Qn_name ||
            response.data?.id ||
            response.data?.data?.question_id ||
            response.data?.data?.Qn_name ||
            response.data?.data?.id;

          // Update currentQuestionId in sessionStorage if we got question_id from response
          let finalQuestionId = questionIdFromResponse;
          if (questionIdFromResponse) {
            sessionStorage.setItem(
              "currentQuestionId",
              String(questionIdFromResponse)
            );
          } else {
            finalQuestionId = sessionStorage.getItem("currentQuestionId");
          }

          // Trigger project_coding_time/update after successful save (CC + AD).
          // Non-critical: never let a secondary failure wipe the session/logout.
          try {
            // Get project_id from sessionStorage
            const storedProjectId = sessionStorage.getItem(
              "contentCreation_projectId"
            );
            const projectId = storedProjectId
              ? JSON.parse(storedProjectId)
              : null;

            // Get question_id: prefer response, fallback to currentQuestionId from sessionStorage
            const questionId = finalQuestionId;

            // Get duration from state (already in minutes)
            const duration = durationMinutes || 0;

            // Only call the endpoint if we have project_id and question_id
            if (projectId && questionId) {
              const timeUpdatePayload = {
                project_id: parseInt(projectId),
                question_id: String(questionId),
                duration: parseInt(duration),
              };

              // Use raw axios-like call via apiPut; backend now allows CC.
              // Still swallow errors so save success is not blocked.
              await apiPut(
                `${config.API_BASE_URL}/api/admin/project_coding_time/update/`,
                timeUpdatePayload
              );
            }
          } catch (timeUpdateError) {
            console.error(
              "Failed to update project coding time (content already saved):",
              timeUpdateError
            );
          }
          
          // Return question_id for approval status update (uploaded, not approved)
          return finalQuestionId;
        } else {
          throw new Error(response.data?.message || "Failed to save");
        }
      } catch (error) {
        setSaveError(
          error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message ||
            "Error saving project"
        );
        throw error; // Re-throw to allow caller to handle
      } finally {
        setIsSaving(false);
      }
    };

    // Expose handleSave via ref
    useImperativeHandle(ref, () => ({
      handleSave: handleSave,
      validateSaveFields: validateProjectCodingRequiredFields,
    }));

    //  --------------------------------------------------------------
    // Step 6: Render - Main Component
    // ---------------------------------------------------------------

    return (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <div
            className="card border-0"
            style={{
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <div
              className="card-header py-2 px-3"
              style={{
                backgroundColor: "#4f46e5",
                color: "white",
                borderRadius: "12px 12px 0 0",
                flexShrink: 0,
              }}
            >
              <h6 className="mb-0 fw-semibold">Project Configuration</h6>
            </div>
            <div
              className="card-body p-3"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <fieldset disabled={isReadOnly} style={{ border: 0, margin: 0, padding: 0 }}>
              {projectImageUrlNull && (
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-primary w-100 btn-sm"
                    onClick={handleOpenDockerImageDialog}
                    style={{ borderRadius: "8px" }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Select Docker Image
                  </button>
                </div>
              )}

              <section
                ref={giteaSectionRef}
                className="mb-3 pb-3"
                style={{ borderBottom: "1px solid #e5e7eb" }}
              >
                <p className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.04em" }}>
                  Repository
                </p>

                <label htmlFor="giteaRepo" className="form-label small fw-semibold text-dark mb-1">
                  Gitea Repository URL <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="giteaRepo"
                  className={`form-control form-control-sm border-2 mb-2${saveFieldHighlight.giteaRepo ? " is-invalid" : ""}`}
                  value={giteaRepoUrl}
                  onChange={(e) => {
                    setGiteaRepoUrl(e.target.value);
                    setContainerError(null);
                    setSaveFieldHighlight((s) =>
                      s.giteaRepo || s.templateRepo ? { ...s, giteaRepo: false, templateRepo: false } : s
                    );
                  }}
                  placeholder="https://gitea.example.com/owner/repo"
                  style={{
                    borderRadius: "8px",
                    borderColor: saveFieldHighlight.giteaRepo ? "#dc3545" : "#e5e7eb",
                  }}
                />

                <label htmlFor="giteaTag" className="form-label small fw-semibold text-dark mb-1">
                  Gitea Version/Release <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="giteaTag"
                  className={`form-control form-control-sm border-2 mb-1${saveFieldHighlight.giteaTag ? " is-invalid" : ""}`}
                  value={giteaTag}
                  onChange={(e) => {
                    setGiteaTag(e.target.value);
                    setContainerError(null);
                    setSaveFieldHighlight((s) =>
                      s.giteaTag ? { ...s, giteaTag: false } : s
                    );
                  }}
                  placeholder="e.g., v1.0.0, release-1.0, main"
                  required
                  style={{
                    borderRadius: "8px",
                    borderColor: saveFieldHighlight.giteaTag ? "#dc3545" : "#e5e7eb",
                  }}
                />
                <small className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
                  Clone a specific release. Validation runs all JSON files in testing_config.
                </small>

                <div ref={templateRepoWrapRef}>
                  <label htmlFor="templateRepoUrl" className="form-label small fw-semibold text-dark mb-1">
                    Template Repo URL <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="templateRepoUrl"
                    className={`form-control form-control-sm border-2${saveFieldHighlight.templateRepo ? " is-invalid" : ""}`}
                    value={templateRepoUrl}
                    onChange={(e) => {
                      setTemplateRepoUrl(e.target.value);
                      setSaveFieldHighlight((s) =>
                        s.templateRepo ? { ...s, templateRepo: false } : s
                      );
                    }}
                    placeholder="https://gitea.exskilence.com/owner/template-repo"
                    required
                    style={{
                      borderRadius: "8px",
                      borderColor: saveFieldHighlight.templateRepo ? "#dc3545" : "#e5e7eb",
                    }}
                  />
                  {isSameRepoUrl(giteaRepoUrl, templateRepoUrl) && (
                    <div className="invalid-feedback d-block" style={{ fontSize: "0.8rem" }}>
                      Template repo URL must be different from the Gitea repository URL.
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-primary w-100 btn-sm mt-3"
                  onClick={handleCreateContainer}
                  disabled={
                    isCreatingContainer ||
                    !giteaRepoUrl ||
                    !giteaTag?.trim()
                  }
                  style={{ borderRadius: "8px" }}
                >
                  {isCreatingContainer ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Creating...
                    </>
                  ) : (
                    "Launch Editor"
                  )}
                </button>
                {containerError && (
                  <div className="alert alert-danger py-2 px-2 mt-2 mb-0 small" role="alert">
                    {containerError}
                  </div>
                )}
              </section>

              <section className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="text-uppercase text-muted small fw-semibold mb-0" style={{ letterSpacing: "0.04em" }}>
                    Task and Requirements
                  </p>
                  <button
                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                    onClick={() => setIsFormCollapsed(!isFormCollapsed)}
                    title={isFormCollapsed ? "Expand form" : "Collapse form"}
                    type="button"
                  >
                    <FontAwesomeIcon
                      icon={isFormCollapsed ? faChevronDown : faChevronUp}
                      size="xs"
                    />
                  </button>
                </div>

              <div
                className="mb-3"
                style={{ display: isFormCollapsed ? "none" : "block" }}
              >
                <label
                  htmlFor="titleName"
                  className="form-label small fw-semibold text-dark mb-1"
                >
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  ref={titleInputRef}
                  type="text"
                  id="titleName"
                  className={`form-control border-2${saveFieldHighlight.taskName ? " is-invalid" : ""}`}
                  value={pageName}
                  onChange={(e) => {
                    setPageName(e.target.value);
                    setSaveFieldHighlight((s) =>
                      s.taskName ? { ...s, taskName: false } : s
                    );
                  }}
                  placeholder="Enter task name (e.g., Simple Calculator)"
                  required
                  style={{
                    borderRadius: "10px",
                    borderColor: saveFieldHighlight.taskName
                      ? "#dc3545"
                      : "#e5e7eb",
                  }}
                />
              </div>

              {/* Requirements */}
              <div
                ref={requirementsWrapRef}
                className="mb-4"
                style={{
                  display: isFormCollapsed ? "none" : "block",
                  borderRadius: "12px",
                  padding: saveFieldHighlight.requirements ? "6px" : undefined,
                  border: saveFieldHighlight.requirements
                    ? "2px solid #dc3545"
                    : undefined,
                  backgroundColor: saveFieldHighlight.requirements
                    ? "rgba(220, 53, 69, 0.04)"
                    : undefined,
                }}
              >
                <label
                  htmlFor="question"
                  className="form-label small fw-semibold text-dark mb-1"
                >
                  Requirements <span className="text-danger">*</span>
                </label>
                <div className="project-sidebar-editor">
                <QuestionEditor
                  resetKey={requirementsResetKey}
                  value={requirementsSeed}
                  onChange={handleRequirementsChange}
                  placeholder="Enter the requirements with detailed instructions"
                  readOnly={isReadOnly}
                  editorHeight={220}
                  autoGrow
                />
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                  <span className="small text-muted">Image URLs</span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={addImageUrl}
                    title="Add Image URLs"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />
                    Image
                  </button>
                </div>
              </div>
              </section>

              <section
                className="mb-2"
                style={{
                  display: isFormCollapsed ? "none" : "block",
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "12px",
                }}
              >
                <p className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.04em" }}>
                  Metadata
                </p>

              {/* Tags Section - matching CodingForm pattern */}
              <div
                ref={tagsWrapRef}
                className="mb-4"
                style={{
                  display: isFormCollapsed ? "none" : "block",
                  borderRadius: "12px",
                  padding: saveFieldHighlight.tags ? "8px" : undefined,
                  border: saveFieldHighlight.tags
                    ? "2px solid #dc3545"
                    : undefined,
                  backgroundColor: saveFieldHighlight.tags
                    ? "rgba(220, 53, 69, 0.04)"
                    : undefined,
                }}
              >
                <h5 className="text-muted">
                  Enter Tags : <span className="text-danger">*</span>
                </h5>
                {tags.map((tag, index) => (
                  <div key={index} className="mb-3 position-relative">
                    <div className="form-group">
                      <label htmlFor={`tag${index}`}>Tag {index + 1}:</label>
                      <input
                        type="text"
                        id={`tag${index}`}
                        className="form-control custom-input mb-2"
                        value={tag}
                        onChange={(e) => handleTagChange(index, e.target.value)}
                        placeholder="Enter tag"
                      />
                      <button
                        className="btn-close position-absolute top-0 end-0"
                        aria-label="Close"
                        onClick={() => removeTag(index)}
                      />
                    </div>
                  </div>
                ))}
                <button
                  className="btn text-light btn-sm mt-1"
                  style={{ backgroundColor: "#377383" }}
                  onClick={addTag}
                  type="button"
                >
                  Add tag
                </button>
              </div>

              {/* Duration Section - matching VideoForm/NotesForm pattern */}
              <div className="mb-3">
                <label htmlFor="durationMinutes" className="form-label small fw-semibold text-dark mb-1">
                  Duration (minutes) <span className="text-danger">*</span>
                </label>
                <input
                  ref={durationInputRef}
                  type="number"
                  id="durationMinutes"
                  className={`form-control form-control-sm custom-input${saveFieldHighlight.duration ? " is-invalid" : ""}`}
                  value={durationMinutes}
                  onChange={(e) => {
                    setDurationMinutes(e.target.value);
                    const nextValue = Number(e.target.value);
                    if (Number.isFinite(nextValue) && nextValue > 0) {
                      setSaveFieldHighlight((s) =>
                        s.duration ? { ...s, duration: false } : s
                      );
                    }
                  }}
                  placeholder="Enter duration in minutes (e.g., 10)"
                  min="1"
                  required
                />
              </div>

              {/* Image URLs Section */}
              {imageUrls.length > 0 && (
                <div
                  className="mb-4"
                  style={{ display: isFormCollapsed ? "none" : "block" }}
                >
                  <div
                    className="border rounded p-3"
                    style={{ backgroundColor: "#f9fafb" }}
                  >
                    {imageUrls.map((imageUrl, index) => (
                      <div
                        key={index}
                        className="mb-3 p-3 border rounded"
                        style={{ backgroundColor: "white" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0 fw-semibold">
                            Image {index + 1}
                          </h6>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeImageUrl(index)}
                            title="Remove this image"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <label className="form-label small fw-semibold">
                              Actual Image URL:
                            </label>
                            <input
                              type="url"
                              className="form-control form-control-sm"
                              value={imageUrl.actualUrl}
                              onChange={(e) =>
                                updateImageUrl(
                                  index,
                                  "actualUrl",
                                  e.target.value
                                )
                              }
                              placeholder="Enter actual image URL"
                              style={{ borderRadius: "8px" }}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label small fw-semibold">
                              Name of Image URL:
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={imageUrl.expectedUrl}
                              onChange={(e) =>
                                updateImageUrl(
                                  index,
                                  "expectedUrl",
                                  e.target.value
                                )
                              }
                              placeholder="Enter name of image URL"
                              style={{ borderRadius: "8px" }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </section>
              </fieldset>
            </div>
          </div>
        </div>

        {/* Docker Image Selection Dialog */}
        {showDockerImageDialog && (
          <div
            className="modal show d-block"
            style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              overflow: "auto",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseDialog();
              }
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                margin: "1.75rem auto",
                maxWidth: "800px",
              }}
            >
              <div
                className="modal-content"
                style={{
                  borderRadius: "15px",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Select Docker Image</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseDialog}
                  ></button>
                </div>
                <div
                  className="modal-body"
                  style={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    maxHeight: "calc(90vh - 120px)",
                  }}
                >
                  {/* Tag Filters */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Filter by Tags:
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {availableTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className={`btn btn-sm ${
                            selectedTags.includes(tag)
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => handleTagToggle(tag)}
                          style={{ borderRadius: "20px" }}
                        >
                          {tag}
                          {selectedTags.includes(tag) && (
                            <FontAwesomeIcon icon={faCheck} className="ms-1" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Build New Image Button */}
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-success w-100"
                      onClick={() => {
                        setShowBuildForm(!showBuildForm);
                        setSelectedDockerImageId(null);
                      }}
                      disabled={buildProgress.imageId !== null}                      style={{ borderRadius: "10px" }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      {showBuildForm ? "Hide Build Form" : "Build New Image"}
                    </button>
                  </div>

                  {/* Build New Image Form */}
                  {showBuildForm && (
                    <div
                      className="border rounded p-3 mb-3"
                      style={{ backgroundColor: "#f9fafb" }}
                    >
                      <h6 className="mb-3 fw-semibold">Build New Docker Image</h6>

                      {/* Image Name and Tag */}
                      <div className="row mb-3">
                        <div className="col-md-8">
                          <label className="form-label small fw-semibold">
                            Image Name: <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={buildImageName}
                            onChange={(e) => setBuildImageName(e.target.value)}
                            placeholder="e.g., my-python-app"
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Tag:
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={buildImageTag}
                            onChange={(e) => setBuildImageTag(e.target.value)}
                            placeholder="latest"
                            style={{ borderRadius: "8px" }}
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">
                          Description:
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          value={buildDescription}
                          onChange={(e) => setBuildDescription(e.target.value)}
                          placeholder="Optional description"
                          rows="2"
                          style={{ borderRadius: "8px" }}
                        />
                      </div>

                      {/* Build Form Tags */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">
                          Tags: <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {availableTags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              className={`btn btn-sm ${
                                buildFormTags.includes(tag)
                                  ? "btn-success"
                                  : "btn-outline-success"
                              }`}
                              onClick={() => handleBuildFormTagToggle(tag)}
                              style={{ borderRadius: "20px" }}
                            >
                              {tag}
                              {buildFormTags.includes(tag) && (
                                <FontAwesomeIcon icon={faCheck} className="ms-1" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Packages Section */}
                      {buildFormTags.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label small fw-semibold">
                            Packages (select from filtered options):
                          </label>
                          {(() => {
                            const { packages } =
                              getPackagesAndExtensionsByTags(buildFormTags);
                            const hasPackages =
                              packages.pip.length > 0 ||
                              packages.npm.length > 0 ||
                              packages.apt.length > 0;

                            if (!hasPackages) {
                              return (
                                <div className="text-muted small">
                                  No packages available for selected tags.
                                </div>
                              );
                            }

                            return (
                              <div
                                className="border rounded p-2"
                                style={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  backgroundColor: "white",
                                }}
                              >
                                {packages.pip.length > 0 && (
                                  <div className="mb-2">
                                    <strong className="small">PIP:</strong>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                      {packages.pip.map((pkg) => {
                                        const pkgKey = `pip:${pkg}`;
                                        const isSelected = selectedPackages.includes(pkgKey);
                                        return (
                                          <label
                                            key={pkgKey}
                                            className={`btn btn-sm ${
                                              isSelected
                                                ? "btn-primary"
                                                : "btn-outline-primary"
                                            }`}
                                            style={{
                                              borderRadius: "15px",
                                              cursor: "pointer",
                                              margin: "2px",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() =>
                                                handlePackageToggle("pip", pkg)
                                              }
                                              style={{ display: "none" }}
                                            />
                                            {isSelected && (
                                              <FontAwesomeIcon
                                                icon={faCheck}
                                                className="me-1"
                                              />
                                            )}
                                            {pkg}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {packages.npm.length > 0 && (
                                  <div className="mb-2">
                                    <strong className="small">NPM:</strong>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                      {packages.npm.map((pkg) => {
                                        const pkgKey = `npm:${pkg}`;
                                        const isSelected = selectedPackages.includes(pkgKey);
                                        return (
                                          <label
                                            key={pkgKey}
                                            className={`btn btn-sm ${
                                              isSelected
                                                ? "btn-primary"
                                                : "btn-outline-primary"
                                            }`}
                                            style={{
                                              borderRadius: "15px",
                                              cursor: "pointer",
                                              margin: "2px",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() =>
                                                handlePackageToggle("npm", pkg)
                                              }
                                              style={{ display: "none" }}
                                            />
                                            {isSelected && (
                                              <FontAwesomeIcon
                                                icon={faCheck}
                                                className="me-1"
                                              />
                                            )}
                                            {pkg}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {packages.apt.length > 0 && (
                                  <div className="mb-2">
                                    <strong className="small">APT:</strong>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                      {packages.apt.map((pkg) => {
                                        const pkgKey = `apt:${pkg}`;
                                        const isSelected = selectedPackages.includes(pkgKey);
                                        return (
                                          <label
                                            key={pkgKey}
                                            className={`btn btn-sm ${
                                              isSelected
                                                ? "btn-primary"
                                                : "btn-outline-primary"
                                            }`}
                                            style={{
                                              borderRadius: "15px",
                                              cursor: "pointer",
                                              margin: "2px",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() =>
                                                handlePackageToggle("apt", pkg)
                                              }
                                              style={{ display: "none" }}
                                            />
                                            {isSelected && (
                                              <FontAwesomeIcon
                                                icon={faCheck}
                                                className="me-1"
                                              />
                                            )}
                                            {pkg}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Extensions Section */}
                      {buildFormTags.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label small fw-semibold">
                            VS Code Extensions (select from filtered options):
                          </label>
                          {(() => {
                            const { extensions } =
                              getPackagesAndExtensionsByTags(buildFormTags);

                            if (extensions.length === 0) {
                              return (
                                <div className="text-muted small">
                                  No extensions available for selected tags.
                                </div>
                              );
                            }

                            return (
                              <div
                                className="border rounded p-2"
                                style={{
                                  maxHeight: "150px",
                                  overflowY: "auto",
                                  backgroundColor: "white",
                                }}
                              >
                                <div className="d-flex flex-wrap gap-1">
                                  {extensions.map((ext) => {
                                    const isSelected = selectedExtensions.includes(ext);
                                    return (
                                      <label
                                        key={ext}
                                        className={`btn btn-sm ${
                                          isSelected
                                            ? "btn-info"
                                            : "btn-outline-info"
                                        }`}
                                        style={{
                                          borderRadius: "15px",
                                          cursor: "pointer",
                                          margin: "2px",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() =>
                                            handleExtensionToggle(ext)
                                          }
                                          style={{ display: "none" }}
                                        />
                                        {isSelected && (
                                          <FontAwesomeIcon
                                            icon={faCheck}
                                            className="me-1"
                                          />
                                        )}
                                        {ext}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Build Button */}
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setShowBuildForm(false);
                            setBuildImageName("");
                            setBuildImageTag("latest");
                            setBuildDescription("");
                            setBuildFormTags([]);
                            setSelectedPackages([]);
                            setSelectedExtensions([]);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={handleBuildImage}
                          disabled={
                            isBuilding ||
                            !buildImageName.trim() ||
                            buildFormTags.length === 0
                          }
                        >
                          {isBuilding ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Creating & Building...
                            </>
                          ) : (
                            "Create and Build"
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Build Progress Bar - Thin horizontal line below form */}
                  {buildProgress.imageId && (
                    <div className="mb-3" style={{ position: "relative", paddingTop: "4px" }}>
                      <div
                        className="progress"
                        style={{
                          height: "1px",
                          backgroundColor: "#e0e0e0",
                          position: "relative",
                          cursor: "help",
                        }}
                        title={`${buildProgress.imageName || "Image"}: ${buildProgress.percentage}% - ${buildProgress.message}`}
                      >
                        <div
                          className={`progress-bar ${
                            buildProgress.status === "failed"
                              ? "bg-danger"
                              : buildProgress.status === "ready"
                              ? "bg-success"
                              : "bg-info"
                          }`}
                          role="progressbar"
                          style={{
                            width: `${buildProgress.percentage}%`,
                            height: "1px",
                            transition: "width 0.5s ease",
                            position: "relative",
                          }}
                          title={`${buildProgress.imageName || "Image"}: ${buildProgress.percentage}% - ${buildProgress.message}`}
                        >
                          {/* Percentage label that moves with progress */}
                          {buildProgress.percentage > 0 && (
                            <span
                              className="position-absolute"
                              style={{
                                right: buildProgress.percentage > 95 ? "-35px" : "0px",
                                top: "-10px",
                                fontSize: "10px",
                                color: buildProgress.status === "failed" 
                                  ? "#dc3545" 
                                  : buildProgress.status === "ready"
                                  ? "#198754"
                                  : "#0dcaf0",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                backgroundColor: "white",
                                padding: "0 2px",
                              }}
                            >
                              {buildProgress.percentage}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Docker Images List */}
                  {!showBuildForm && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Available Images:
                      </label>
                      {isLoadingImages ? (
                        <div className="text-center py-4">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : dockerImages.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          No images found. Try adjusting your tag filters.
                        </div>
                      ) : (
                        <div
                          className="list-group"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          {dockerImages.map((image) => (
                            <button
                              key={image.id}
                              type="button"
                              className={`list-group-item list-group-item-action ${
                                selectedDockerImageId === image.id
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => setSelectedDockerImageId(image.id)}
                              style={{
                                borderRadius: "10px",
                                marginBottom: "5px",
                                cursor: "pointer",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>
                                    {image.image_name}:{image.image_tag}
                                  </strong>
                                  {image.description && (
                                    <div className="small text-muted mt-1">
                                      {image.description}
                                    </div>
                                  )}
                                </div>
                                {selectedDockerImageId === image.id && (
                                  <FontAwesomeIcon icon={faCheck} />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div
                  className="modal-footer"
                  style={{ flexShrink: 0, borderTop: "1px solid #dee2e6" }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDockerImageSelect}
                    disabled={
                      (!selectedDockerImageId && !showBuildForm) ||
                      isLoadingImages ||
                      isBuilding
                    }
                  >
                    {showBuildForm ? "Close" : "Select Image"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

ProjectSidebar.displayName = "ProjectSidebar";

export default ProjectSidebar;
